import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./FarmListPage.scss";
import BASE_URL from "../../../config/apiConfig";
import axios from "axios";
import { getUserEmail } from "../../../utils/auth";

const FarmListPage = () => {
  const [farms, setFarms] = useState([]);
  const [filteredFarms, setFilteredFarms] = useState([]);
  const [cropOptions, setCropOptions] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState("all");
  const [farmNameFilter, setFarmNameFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [farmToDelete, setFarmToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isTrialUser, setIsTrialUser] = useState(false);

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchCrops = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/crop-management/get-all-crops`,
      );
      setCropOptions(response.data || []);
    } catch (error) {
      console.error("Error fetching crops:", error);
    }
  };

  const fetchCustomerDetails = async () => {
    const userEmail = getUserEmail();
    if (!userEmail) return;

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/get-customer-details`,
        { email: userEmail },
      );
      setIsTrialUser(response.data.is_trial);
    } catch (error) {
      console.error("Failed to fetch customer details:", error);
    }
  };

  const fetchFarms = async () => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      console.error("User email is missing.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-all-added-farms`,
        { email: userEmail },
      );

      const rawFarms = response.data;

      const farmsWithLocation = await Promise.all(
        rawFarms.map(async (farm) => {
          const locationDetails = await getLocationFromCoordinates(
            farm.coordinates.lat,
            farm.coordinates.lng,
          );
          return { ...farm, locationDetails };
        }),
      );

      const sortedFarms = farmsWithLocation.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      setFarms(sortedFarms);
      applyFilters(sortedFarms);
    } catch (error) {
      console.error("Failed to fetch farms:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCrops();
    fetchFarms();
    fetchCustomerDetails();
  }, []);

  const applyFilters = (farmList = farms) => {
    const filtered = farmList.filter((farm) => {
      const matchesCrop =
        selectedCrop === "all" || farm.crop_code === selectedCrop;

      return matchesCrop;
    });

    setFilteredFarms(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [selectedCrop, farms]);

  const handleFarmClick = (farm_id, sowingDate, map_image_url) => {
    navigate(`/user/crop-health-management/farm/${farm_id}`, {
      state: { sowingDate, map_image_url },
    });
  };

  const handleRenewalPayment = (fieldId) => {
    navigate(`/user/renew-farm/farm/${fieldId}`, {
      state: { status: "renew" },
    });
  };

  const handleNewPayment = (farm) => {
    if (!farm) return;

    navigate("/payment", {
      state: {
        farm_id: farm.id,
        cropCode: farm.crop_code,
        cropName: farm.crop_name,
        farmName: farm.field_name,
        totalPrice: farm.total_price,
        cropType: farm.plan_name,
        sowingDate: farm.sowing_date,
        paymentFrequency: farm.payment_frequency,
        coordinates: farm.coordinates,
        fieldArea: farm.field_area,
      },
    });
  };

  const getLocationFromCoordinates = async (lat, lng) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
      );

      const result = response.data.results[0];
      const addressComponents = result.address_components;

      const state = addressComponents.find((comp) =>
        comp.types.includes("administrative_area_level_1"),
      )?.long_name;

      const town = addressComponents.find((comp) =>
        comp.types.includes("locality"),
      )?.long_name;

      return {
        display_name: result.formatted_address,
        state,
        town,
      };
    } catch (error) {
      console.error("Google reverse geocoding failed:", error);
      return null;
    }
  };

  return (
    <div className="farm-list-page">
      <div className="farm-list-hero">
        <div className="farm-list-hero-grid" />

        <div className="farm-list-hero-content">
          <span className="farm-list-eyebrow">FARMOLOGY PLATFORM</span>
          <h1>Your farms, at a glance.</h1>
          <p>
            Manage your fields, review farm intelligence, and keep every
            subscription in one place.
          </p>
        </div>

        <div className="farm-list-hero-meta">
          <span>MY FARMS</span>
          <span>{filteredFarms.length} FARM(S)</span>
        </div>
      </div>

      <main className="farm-list-main">
        <div className="farm-list-heading-row">
          <div>
            <span className="farm-list-section-label">YOUR FARM DATA</span>
            <h2>Manage your farms</h2>
            <p>
              Select an active farm to view its crop health and farm insights.
            </p>
          </div>

          <div className="farm-list-btn-group">
            {isLoading ? (
              <p />
            ) : (
              <>
                <button
                  className="farm-list-add-btn"
                  onClick={() => navigate("/user/add-farm-now")}
                >
                  + Add Farm
                </button>

                {farms.length === 0 && !isTrialUser && (
                  <button
                    className="farm-list-trial-btn"
                    onClick={() => navigate("/user/trial-add-farm")}
                  >
                    + Add Trial Farm
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="farm-list-success-message">{successMessage}</div>
        )}

        <div className="farm-list-container">
          {isLoading ? (
            <div className="farm-list-state">
              <span className="farm-list-spinner" />
              <strong>Loading your farms</strong>
              <span>Fetching your latest farm information.</span>
            </div>
          ) : filteredFarms.length > 0 ? (
            <div className="farm-list-grid">
              {filteredFarms.map((farm) => {
                const isExpired = farm.plan_status === "renew";
                const isPendingPayment = farm.plan_status === "created";

                return (
                  <article
                    key={farm.field_id}
                    className={`farm-list-item ${
                      isExpired ? "farm-expired" : ""
                    } ${isPendingPayment ? "farm-pending" : ""}`}
                    onClick={() => {
                      if (farm.plan_status === "active") {
                        handleFarmClick(
                          farm.id,
                          farm.sowing_date,
                          farm.map_image_url,
                        );
                      }
                    }}
                  >
                    <div className="farm-list-card-head">
                      <div>
                        <span className="farm-card-kicker">FARM</span>
                        <h3 className="farm-list-title">{farm.field_name}</h3>
                      </div>

                      <div className="farm-card-status">
                        {isExpired && (
                          <span className="farm-expired-badge">
                            Subscription Expired
                          </span>
                        )}

                        {isPendingPayment && (
                          <button
                            className={`farm-list-pay-btn ${farm.plan_status}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNewPayment(farm);
                            }}
                          >
                            Pay Now
                          </button>
                        )}

                        {isExpired && (
                          <button
                            className="farm-list-pay-btn renew"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenewalPayment(farm.field_id);
                            }}
                          >
                            Renew Now
                          </button>
                        )}

                        {farm.plan_status === "active" && (
                          <span className="farm-list-active-badge">Active</span>
                        )}
                      </div>
                    </div>

                    <div className="farm-list-card-body">
                      <div className="farm-list-map">
                        {farm.map_image_url ? (
                          <a
                            href={`https://www.google.com/maps?q=${farm.coordinates.lat},${farm.coordinates.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="farm-map-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <img
                              src={farm.map_image_url}
                              alt={`${farm.field_name} map`}
                              className="farm-list-map-image"
                            />
                            <span className="map-overlay-btn">
                              📍 View on Map
                            </span>
                          </a>
                        ) : (
                          <div className="farm-list-map-empty">
                            Map preview unavailable
                          </div>
                        )}
                      </div>

                      <div className="farm-list-details">
                        <div className="farm-detail-item">
                          <span>Crop</span>
                          <strong>{farm.crop_name}</strong>
                        </div>

                        <div className="farm-detail-item">
                          <span>Area</span>
                          <strong>
                            {Number(farm.field_area).toFixed(2)} ha
                          </strong>
                        </div>

                        <div className="farm-detail-item">
                          <span>Plan</span>
                          <strong className="capitalize">
                            {farm.plan_name}
                          </strong>
                        </div>

                        <div className="farm-detail-item">
                          <span>
                            {farm.crop_name.toLowerCase() === "tea"
                              ? "Pruning Date"
                              : "Sowing Date"}
                          </span>
                          <strong>{formatDate(farm.sowing_date)}</strong>
                        </div>

                        <div className="farm-detail-item">
                          <span>Purchased</span>
                          <strong>{formatDate(farm.created_at)}</strong>
                        </div>

                        <div className="farm-detail-item">
                          <span>Frequency</span>
                          <strong>{farm.payment_frequency} Month(s)</strong>
                        </div>

                        <div className="farm-detail-item">
                          <span>Amount</span>
                          <strong>₹ {farm.total_price}</strong>
                        </div>

                        <div className="farm-detail-item farm-detail-wide farm-detail-location">
                          <span>Location & Coordinates</span>
                          <div className="farm-location-content">
                            <strong className="farm-location-address">
                              {farm.address || "Location unavailable"}
                            </strong>
                            <span className="farm-location-coordinates">
                              {farm.coordinates?.lat}, {farm.coordinates?.lng}
                            </span>
                          </div>
                        </div>

                        {farm.plan_status !== "created" && (
                          <div className="farm-detail-item farm-detail-wide farm-detail-expiry">
                            <span>Subscription Expiry</span>
                            <strong>{formatDate(farm.expires_at)}</strong>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="farm-card-footer">
                      <span>
                        {farm.plan_status === "active"
                          ? "Open farm intelligence"
                          : farm.plan_status === "created"
                            ? "Complete payment to activate"
                            : "Subscription expired — renew to continue"}
                      </span>
                      <span aria-hidden="true">↗</span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="farm-list-empty">
              <div className="farm-empty-icon">+</div>
              <h3>No farms yet</h3>
              <p>
                No farms are available right now. Add your farm to get started
                with Farmology.
              </p>
              <button
                className="farm-list-add-btn"
                onClick={() => navigate("/user/add-farm-now")}
              >
                + Add Farm
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FarmListPage;
